/*
 * Copyright (c) 2021 SARL SIGMA POLYNESIA
 */

import { useCallback, useMemo } from 'react'
import {
  deleteInvoiceFileById,
  downloadInvoiceFileById,
  findManyInvoiceFiles,
  uploadInvoiceFile
} from '../../lib/rest/invoiceFileAPIClient'
import FileInput, { FileInputProps } from '../inputs/FileInput'
import { useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import { useSettingsContext } from '../SettingsProvider'

export type InvoiceFileFieldProps =
  Omit<FileInputProps, 'onChange' | 'onDelete' | 'onDownload' | 'onUpload' | 'contentLength' | 'lastModified'>
  & {
  invoiceId?: number,
  tmpRef: string
}

function InvoiceFileField (props: InvoiceFileFieldProps) {
  const { invoiceId, tmpRef, ...others } = props

  const { settings } = useSettingsContext()

  const filters = useMemo(() => (
    invoiceId > 0
      ? { invoiceId }
      : { tmpRef }
  ), [invoiceId, tmpRef])

  const loader = useQuery({
    enabled: true,
    queryKey: ['invoiceFile', filters],
    queryFn: () => findManyInvoiceFiles(filters)
      .then((json) => (getHalEmbedded(json)?.files || [])[0] || {} as Partial<InvoiceFile>),
    refetchOnWindowFocus: false
  })
  const { data: file } = loader
  const fileId = file?.id

  const downloadFunction = useCallback(() => downloadInvoiceFileById(fileId), [fileId])
  const deleteFunction = useCallback(() => deleteInvoiceFileById(fileId), [fileId])
  const uploadFunction = useCallback((file1: File) => uploadInvoiceFile(file1, tmpRef, invoiceId), [invoiceId, tmpRef])

  return (
    <FileInput
      {...others}
      accept={settings.INVOICE_FILE_TYPES}
      maxSize={settings.INVOICE_MAX_UPLOAD_SIZE}
      name="invoice_file"
      onDownload={downloadFunction}
      onDelete={deleteFunction}
      onChange={() => loader.refetch()}
      onUpload={uploadFunction}
      value={file?.name}
      lastModified={file?.createdAt}
      contentLength={file?.size}
    />
  )
}

export default InvoiceFileField
